import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spinner, Badge } from '../../components/UI';
import PhaseIllustration from '../../components/illustrations/PhaseIllustration';
import { api } from '../../utils/api';

// The phase overview page (WI-C1): (1) The line — carried-over phase text,
// (2) My work here — the caller's CRM projects at this phase's stages.
// Band 3 "Notes & tools" je premešten u PhaseNotes i renderuje se u
// podsekcijama (Explore → Prospecting, Master → Proposals, Deliver → Proposals).

// Stage anchor → phase (same keyword mapper as My Line; stages are still free
// strings until the CRM process model lands). Deliver → Master → else Explore.
function phaseOf(p) {
  if (p.closedWonDate) return 'D';
  const s = (p.stage || '').toLowerCase();
  if (/deliver|closed|won|graduat|live|complete/.test(s)) return 'D';
  if (/profile|proposal|programme|program|negoti|offer|adapt/.test(s)) return 'M';
  return 'E';
}

const PHASE = {
  E: {
    label: 'Explore', tagline: 'promote & prospect',
    intro: 'How focused are we on defining the ideal customer criteria and profile and finding the right prospects by assessing the market? Success, as with every other journey, starts with the first step. Successful prospecting will help us take the right step.',
    points: [
      ['Proactive Prospecting', 'The Ice Pilot must be skilled in various prospecting techniques, moving beyond initial outreach to leverage FAR (Fully Active Recommendation) strategies — referrals from satisfied clients. This requires building strong client relationships and demonstrating the value of Hansen Beck’s services.'],
      ['Comprehensive Content Knowledge', 'A deep understanding of Hansen Beck’s diverse training programmes is crucial. The Ice Pilot must be able to identify the appropriate programme for various organizational roles and client needs.'],
      ['Self-Awareness and Personal Development', 'The Ice Pilot must possess strong self-awareness, recognize personal limitations, and act on them.'],
    ],
  },
  M: {
    label: 'Master', tagline: 'sell & adapt',
    intro: 'This phase employs a structured approach to client engagement, focusing on these key activities:',
    points: [
      ['Client Needs Assessment', 'Expertly conducts needs assessments using open-ended questions and active listening, effectively identifying client challenges and objectives.'],
      ['Programme Content Development', 'Designs and develops a tailored training programme based on the Ideal Profile, incorporating diverse learning activities and addressing various learning styles.'],
      ['Proposing and Closing the Deal', 'Presents a clear, tailored proposal that reflects the insights gathered, then leads the client through the decision toward mutual agreement.'],
    ],
  },
  D: {
    label: 'Deliver', tagline: 'excellence in the room',
    intro: 'This phase focuses on flawless execution of the tailored development programme as outlined in the Master phase. This requires impeccable organizational skills, effective communication, and the ability to adapt to changing circumstances.',
    points: [],
  },
};

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-faint)', margin: '4px 0 10px' }}>
    {children}
  </div>
);

export default function PhaseOverview({ phase }) {
  const cfg = PHASE[phase];
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    api.explore.getProjects()
      .then(d => setProjects((d.projects || []).filter(p => phaseOf(p) === phase)))
      .catch(() => setProjects([]));
  }, [phase]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
      {/* Band 1 — The line */}
      <section>
        <SectionLabel>The line · {cfg.tagline}</SectionLabel>
        <Card style={{ padding: '18px 22px' }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 320px', minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '0.92rem', lineHeight: 1.6, color: 'var(--ink)' }}>{cfg.intro}</p>
              {cfg.points.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 14 }}>
                  {cfg.points.map(([t, b]) => (
                    <div key={t}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--ink)' }}>{t}</div>
                      <div style={{ fontSize: '0.86rem', color: 'var(--ink-soft)', lineHeight: 1.55 }}>{b}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {(phase === 'E' || phase === 'M') && (
              <div style={{ flex: '0 0 150px', alignSelf: 'center', color: 'var(--ink)', opacity: 0.85 }}>
                <PhaseIllustration phase={phase} />
              </div>
            )}
          </div>
        </Card>
      </section>

      {/* Band 2 — My work here */}
      <section>
        <SectionLabel>My work here</SectionLabel>
        {projects === null ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner /></div>
        ) : projects.length === 0 ? (
          <Card style={{ padding: '16px 20px' }}>
            <span style={{ fontSize: '0.88rem', color: 'var(--ink-faint)' }}>No projects in this phase right now.</span>
          </Card>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {projects.map(p => (
              <Card key={p.id} hover onClick={() => navigate('/customers/journeys')} style={{ padding: '13px 15px' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--ink)' }}>{p.name}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--ink-soft)' }}>{p.customer}</div>
                <div style={{ fontSize: '0.76rem', color: 'var(--ink-faint)', marginTop: 4 }}>
                  {p.stage || '—'}{p.nextStep ? ` → ${p.nextStep}` : ''}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
